import React from "react";
import BackButton from "./BackButton";

/**
 * Reusable Page Header Component
 *
 * Provides consistent header layout across all pages with:
 * - Optional back button
 * - Title
 * - Optional subtitle/description
 * - Optional action button
 * - Optional search bar (integrated below title)
 * - Card-like design with rounded corners
 *
 * @param {boolean} showBackButton - Show/hide back button (default: true)
 * @param {string} backTo - Navigation path for back button (default: -1)
 * @param {string} title - Page title (required)
 * @param {string|React.ReactNode} subtitle - Optional subtitle/description
 * @param {React.ReactNode} action - Optional action button/component
 * @param {React.ReactNode} search - Optional search component to integrate
 * @param {string} className - Optional additional classes
 */
const PageHeader = ({
  showBackButton = true,
  backTo = -1,
  onBack,
  title,
  subtitle,
  action,
  search,
  className = "",
}) => {
  return (
    <div className={`bg-white rounded-lg shadow-sm p-6 mb-6 ${className}`}>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-3">
            {showBackButton && <BackButton to={backTo} onClick={onBack} />}
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {title}
            </h1>
          </div>
          {subtitle && (
            <div className="text-gray-600 mt-2 ml-0 sm:ml-14">{subtitle}</div>
          )}
        </div>
        {action && <div className="flex-shrink-0 mt-2 sm:mt-0">{action}</div>}
      </div>

      {/* Optional Search Bar - Aligned with title on desktop */}
      {search && <div className="mt-4 ml-0 sm:ml-14">{search}</div>}
    </div>
  );
};

export default PageHeader;
